import Button from '@orn/shared-web/components/Button/Button';
import { RadioButtonCard, RadioGroup } from '@orn/shared-web/components/RadioButton/RadioButtons';

import { SubscriptionInterval } from '../../../__generated__/globalTypes';

export interface IPlanOption {
  interval: SubscriptionInterval;
  /** Server-formatted, e.g. "$99.00/year" — rendered verbatim. */
  amountDisplay: string;
  trialDays: number;
}

const INTERVAL_LABEL: Record<SubscriptionInterval, string> = {
  [SubscriptionInterval.Annual]: 'Annual',
  [SubscriptionInterval.Monthly]: 'Monthly',
};

/** Annual first — it is the pre-selected, discounted option. */
const INTERVAL_ORDER: readonly SubscriptionInterval[] = [SubscriptionInterval.Annual, SubscriptionInterval.Monthly];

/**
 * Screen 2 of checkout — monthly or annual.
 *
 * One decision per screen: the account is already settled, so this only picks
 * the plan. Amounts are rendered exactly as the server formatted them, because
 * the same helpers write the figure onto the mandate evidence row.
 */
export interface IPlanPickerBaseProps {
  /** One entry per offered interval. Annual is presented first. */
  plans: readonly IPlanOption[];
  selectedInterval: SubscriptionInterval;
  onSelectInterval: (interval: SubscriptionInterval) => void;
  onContinue: () => void;
  /** Returns to the previous checkout screen with the selection kept. */
  onBack: () => void;
}

const PlanPickerBase = ({ plans, selectedInterval, onSelectInterval, onContinue, onBack }: IPlanPickerBaseProps) => {
  const ordered = INTERVAL_ORDER.map((interval) => plans.find((plan) => plan.interval === interval)).filter(
    (plan): plan is IPlanOption => plan !== undefined
  );
  const trialDays = ordered[0]?.trialDays;

  return (
    <div className="flex w-full flex-col gap-6">
      {trialDays !== undefined && (
        <div className="bg-utility-brand-50 flex w-full flex-col items-center gap-1 rounded-xl px-4 py-3">
          <p className="text-primary text-md font-medium">Free for {trialDays} days</p>
          <p className="text-tertiary text-sm">$0 today.</p>
        </div>
      )}

      <RadioGroup
        aria-label="Billing interval"
        value={selectedInterval}
        onChange={(value) => onSelectInterval(value as SubscriptionInterval)}
        className="w-full"
      >
        {ordered.map((plan) => (
          <RadioButtonCard
            key={plan.interval}
            value={plan.interval}
            label={INTERVAL_LABEL[plan.interval]}
            hint={plan.amountDisplay}
          />
        ))}
      </RadioGroup>

      <Button type="button" size="xl" color="primary" className="w-full" onClick={onContinue}>
        Continue
      </Button>
      <Button type="button" size="md" color="link-gray" className="w-full" onClick={onBack}>
        Back
      </Button>
    </div>
  );
};

PlanPickerBase.displayName = 'PlanPickerBase';

export default PlanPickerBase;
